import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../environments/environment';
import { SwPush } from '@angular/service-worker';
import { lastValueFrom, of, take, timeout, BehaviorSubject, Observable } from 'rxjs';

export class EncodeResult {
  token: string = "";
  expires: number = 0;
  issued: number = 0;
  id: number = 0;
  email: string = "";
  username: string = "";
  firstname: string = "";
  isSuperAdmin: boolean = false;
  isAdmin: boolean = false;
  isActive: boolean = false;
  roles: {
    idCompte: number,
    isAdherent: boolean,
    isSocio: boolean,
    isUtilisateur: boolean
  } = { idCompte: 0, isAdherent: false, isSocio: false, isUtilisateur: false };
}

export class LogInBackDto {
  status: number = 0;
  message: string = "";
  content: EncodeResult|undefined = undefined;
}

export enum UserVariable {
  TOKEN = 'token',
  EXPIRES = 'expires',
  ISSUED = 'issued',
  ID = 'id',
  EMAIL = 'email',
  USERNAME = 'username',
  FIRSTNAME = 'firstname',
  ISACTIVE = 'isActive',
  PATH = 'path',
  ROLES = 'roles',
}

@Injectable({
  providedIn: 'root'
})
export class ConnectorService {
  // Subject pour notifier les changements de statut de connexion
  private connectionStatusSubject: BehaviorSubject<boolean>;
  public connectionStatus$: Observable<boolean>;

  constructor(
    private http: HttpClient,
    private router: Router,
    private swPush: SwPush
  ) {
    // Initialiser le BehaviorSubject dans le constructeur
    this.connectionStatusSubject = new BehaviorSubject<boolean>(false);
    this.connectionStatus$ = this.connectionStatusSubject.asObservable();
    
    // Mettre à jour la valeur initiale après l'initialisation
    setTimeout(() => {
      try {
        this.connectionStatusSubject.next(this.isConnected());
      } catch (error) {
        console.error('Erreur lors de l\'initialisation du statut de connexion:', error);
        this.connectionStatusSubject.next(false);
      }
    }, 0);
  }

  connection(login:string|null, password:string|null, callbackClass: any){
    this.http.post<LogInBackDto>(environment.backAPI+'/compte/login', {
      email: login?.trim(),
      password: password
    }).subscribe({
      next: result => {
        if(result.status === 200 && result.content != undefined){
          this.initSession(result);
          this.connectedNavigation();
          callbackClass.connectionCallBack(true);
        }
        else{
          callbackClass.connectionCallBack(false);
        }
      },
      error: error => {
        callbackClass.connectionCallBack(false);
      }
    });
  }

  async inline_connection(login:string|null, password:string|null, parent:any, postConnection?: Function){
    return await this.http.post<LogInBackDto>(environment.backAPI+'/compte/login', {
      email: login?.trim(),
      password: password
    }).subscribe({
      next: (result) => {
        if(result.status === 200 && result.content != undefined){
          this.initSession(result);
          parent.message = "OK";
          if(postConnection) postConnection(true);
        }
        else{
          parent.message = "wrong_credentials";
          if(postConnection) postConnection(false);
        }
      },
      error: (error) => {
        parent.message = "wrong_credentials";
        if(postConnection) postConnection(false);
      }
    });
  }

  // Guards against connectedNavigation() being called twice concurrently
  // (e.g. once from connection()'s success handler and once from
  // LoginComponent.ngOnInit() checking isConnected() on mount) — without
  // this, both calls could each trigger their own router.navigate(), and
  // Angular silently cancels whichever navigation gets superseded, which
  // could leave the outlet stuck on the login screen even though the token
  // is already valid.
  private connectedNavigationInFlight: Promise<void> | null = null;

  async connectedNavigation(): Promise<void> {
    if (this.connectedNavigationInFlight) {
      return this.connectedNavigationInFlight;
    }
    this.connectedNavigationInFlight = this.doConnectedNavigation().finally(() => {
      this.connectedNavigationInFlight = null;
    });
    return this.connectedNavigationInFlight;
  }

  private async doConnectedNavigation(): Promise<void> {
    if(this.isJourney()){
      await this.journeyComeback();
      return;
    }
    const target = (this.getValue(UserVariable.ISACTIVE) === 'Y') ? '/home' : '/account';
    await this.navigateOrFallback(target);
  }

  /**
   * Navigates to url, verifying the navigation actually completed (Angular's
   * Router silently resolves to false — no error — if it cancels a
   * navigation, e.g. because a newer one superseded it). Falls back to a
   * full reload via location.href so the user is never left stuck on a
   * stale screen after a successful login.
   */
  private async navigateOrFallback(url: string): Promise<void> {
    try {
      const success = await this.router.navigateByUrl(url);
      if (!success) {
        console.error(`Navigation vers ${url} échouée, tentative alternative...`);
        window.location.href = window.location.origin + window.location.pathname + '#' + url;
      }
    } catch (error) {
      console.error('Erreur de navigation:', error);
      window.location.href = window.location.origin + window.location.pathname + '#' + url;
    }
  }

  async journeyConnection(){
    console.log('journeyConnection - URL actuelle:', this.router.url);
    localStorage.setItem(UserVariable.PATH, this.router.url);
    console.log('journeyConnection - Redirection vers /login...');
    try {
      const success = await this.router.navigateByUrl('/login');
      console.log('journeyConnection - Navigation réussie:', success);
      if (!success) {
        console.error('journeyConnection - Navigation échouée, tentative alternative...');
        // Forcer un rechargement complet avec le hash (useHash: true)
        window.location.href = window.location.origin + window.location.pathname + '#/login';
      }
    } catch (error) {
      console.error('journeyConnection - Erreur de navigation:', error);
      // Forcer un rechargement complet avec le hash (useHash: true)
      window.location.href = window.location.origin + window.location.pathname + '#/login';
    }
  }

  isJourney(){
    return localStorage.getItem(UserVariable.PATH) !== null;
  }

  async journeyComeback(){
    const path = localStorage.getItem(UserVariable.PATH);
    localStorage.removeItem(UserVariable.PATH);
    await this.navigateOrFallback(path || '/home');
  }

  initSession(result: LogInBackDto){
    if(result.content != undefined){
      localStorage.setItem(UserVariable.TOKEN, result.content.token);
      localStorage.setItem(UserVariable.EXPIRES, result.content.expires.toString());
      localStorage.setItem(UserVariable.ISSUED, result.content.issued.toString());
      localStorage.setItem(UserVariable.ID, result.content.id.toString());
      localStorage.setItem(UserVariable.EMAIL, result.content.email);
      localStorage.setItem(UserVariable.USERNAME, result.content.username);
      localStorage.setItem(UserVariable.FIRSTNAME, result.content.firstname || '');
      localStorage.setItem(UserVariable.ISACTIVE, (result.content.isActive)?'Y':'N');
      localStorage.setItem(UserVariable.ROLES, JSON.stringify(result.content.roles));
      
      // Notifier le changement de statut de connexion
      this.connectionStatusSubject.next(true);
    }
  }

  // Déconnexion en cours, partagée : l'appli appelle disconnection() à des dizaines
  // d'endroits (gestionnaires d'erreur) et plusieurs appels échouent souvent ensemble.
  private disconnecting: Promise<void> | null = null;

  disconnection(): Promise<void> {
    if (!this.disconnecting) {
      this.disconnecting = this.doDisconnection().finally(() => (this.disconnecting = null));
    }
    return this.disconnecting;
  }

  private async doDisconnection(){
    console.log('Début de la déconnexion...');

    // Sans jeton (déjà déconnecté), la désinscription serait refusée : on l'évite (elle
    // partait en rafale avec « Bearer undefined »)
    if (this.token()) try {
      // Tentative de désinscription des notifications avec timeout de 3s
      // Si le Service Worker n'est pas actif, swPush.subscription ne résout jamais
      const endpoint = await lastValueFrom(
        this.getCurrentNotificationSubscription().pipe(timeout(3000))
      );
      if (endpoint) {
        console.log('Désinscription des notifications...');
        try {
          await lastValueFrom(this.http.post(environment.backAPI+'/notifications/unsubscription', { endpoint }, {
            headers: {
              'Authorization': 'Bearer '+this.token()
            }
          }));
          console.log('Désinscription des notifications réussie');
        } catch (error) {
          console.log('Erreur lors de la désinscription des notifications:', error);
        }
      } else {
        console.log('Aucun endpoint de notification à désinscrire');
      }
    } catch (error) {
      console.log('Timeout ou erreur lors de la récupération de l\'endpoint de notification, on continue...');
    }

    // Nettoyage du localStorage
    localStorage.removeItem(UserVariable.TOKEN);
    localStorage.removeItem(UserVariable.EXPIRES);
    localStorage.removeItem(UserVariable.ISSUED);
    localStorage.removeItem(UserVariable.ID);
    localStorage.removeItem(UserVariable.EMAIL);
    localStorage.removeItem(UserVariable.USERNAME);
    localStorage.removeItem(UserVariable.FIRSTNAME);
    localStorage.removeItem(UserVariable.ISACTIVE);
    localStorage.removeItem(UserVariable.ROLES);

    // Notifier le changement de statut de connexion
    this.connectionStatusSubject.next(false);

    // Redirection vers la page de connexion
    console.log('Redirection vers la page de connexion...');
    await this.journeyConnection();
  }

  getCurrentNotificationSubscription() {
    return this.swPush.isEnabled
      ? this.swPush.subscription.pipe(take(1))
      : of(null);
  }

  async check(){
    await this.http.post<LogInBackDto>(environment.backAPI+'/compte/check', {
      token: localStorage.getItem('token')
    }).subscribe(
      (result) => {
        if(result.status === 200 && result.content!= undefined){
          return true;
        }
        else{
          this.disconnection();
          return false;
        }
      }
    );
  }

  getId(){
    return (localStorage.getItem('id') === null)?0:Number(localStorage.getItem('id'));
  }

  getValue(variableName:UserVariable):any{
    const value:string|null = localStorage.getItem(variableName);
    if(value === null) return '';
    return value;
  }

  isConnected(){
    const token = this.token();
    if (!token) {
      return false;
    }
    
    // Vérifier l'expiration du token
    const expires = localStorage.getItem(UserVariable.EXPIRES);
    if (!expires) {
      return false;
    }
    
    // Le back donne l'expiration en millisecondes (comparée ici à tort en secondes :
    // une session expirée n'était jamais détectée, l'appli enchaînait des appels refusés)
    // + 7 jours de grâce : le back accepte encore le jeton pour le renouveler (session
    // glissante) ; au-delà seulement, la session est vraiment terminée.
    const expirationTime = this.expiryMs(expires) + ConnectorService.GRACE_MS;
    const currentTime = Date.now();
    
    // Si le token est expiré, déconnecter l'utilisateur
    if (currentTime >= expirationTime) {
      console.log('Token expiré, déconnexion automatique...');
      this.disconnection();
      return false;
    }
    
    return true;
  }

  isAdherent(){
    const roles = localStorage.getItem(UserVariable.ROLES);
    if(roles === null || roles === 'null') return false;
    return this.isConnected() && JSON.parse(roles).isAdherent;
  }

  isSocio(){
    const roles = localStorage.getItem(UserVariable.ROLES);
    if(roles === null || roles === 'null') return false;
    return this.isConnected() && (roles)?JSON.parse(roles).isSocio:false;
  }

  // Période de grâce du back (checkExpirationStatus) pendant laquelle un jeton expiré est renouvelé
  private static readonly GRACE_MS = 7 * 24 * 60 * 60 * 1000;

  /** Expiration en millisecondes (tolère une ancienne valeur en secondes). */
  private expiryMs(expires: string): number {
    const value = parseInt(expires, 10);
    return value < 1e12 ? value * 1000 : value;
  }

  /** Jeton renouvelé par le back (session glissante, en-tête X-Refresh-Token). */
  updateSession(token: string, expires: number): void {
    localStorage.setItem('token', token);
    localStorage.setItem(UserVariable.EXPIRES, String(expires));
  }

  token(){
    if(localStorage.getItem('token')){
      return localStorage.getItem('token');
    }
    return undefined;
  }

  /**
   * Vérifie si le token est expiré
   */
  isTokenExpired(): boolean {
    const expires = localStorage.getItem(UserVariable.EXPIRES);
    if (!expires) {
      return true;
    }
    
    // Session vraiment terminée : au-delà de la période de grâce (sinon le back la renouvelle)
    return Date.now() >= this.expiryMs(expires) + ConnectorService.GRACE_MS;
  }

  /**
   * Vérifie si le token va expirer dans les prochaines minutes
   */
  isTokenExpiringSoon(minutes: number = 5): boolean {
    const expires = localStorage.getItem(UserVariable.EXPIRES);
    if (!expires) {
      return true;
    }
    
    const timeUntilExpiry = (this.expiryMs(expires) - Date.now()) / 1000;
    
    return timeUntilExpiry <= (minutes * 60);
  }

  async reinit(email:string, code:string){
    await this.http.post<boolean>(environment.backAPI+'/compte/reinit-pwd', {
      email: email,
      code: code
    }).subscribe(
      (result) => {
        return result;
      }
    );
  }
}
